/** Canonical evidence and cross-host fixtures for Elevate contract v1. */
import {
  ELEVATE_CAPABILITIES,
  ELEVATE_CONTRACT_VERSION,
  type ElevateCapability,
  type ElevateCapabilityEvidence,
  type ElevateCapabilityState,
  type ElevateHostProfile,
  type ElevateItem,
} from './elevate.js'
import type { DocumentAddOffer, DocumentEntry } from './documents.js'

const measured = (
  states: Readonly<Record<ElevateCapability, ElevateCapabilityState>>,
  evidence: Readonly<Partial<Record<ElevateCapability, string>>>,
): ElevateCapabilityEvidence[] => ELEVATE_CAPABILITIES.map((capability) => ({
  capability,
  state: states[capability],
  evidence: evidence[capability] ?? 'Measured in the host revision named by this profile.',
}))

const d365States: Record<ElevateCapability, ElevateCapabilityState> = {
  'scope.record': 'live',
  'scope.mine': 'live',
  'scope.teams': 'live',
  'scope.queues': 'live',
  'card.provenance': 'live',
  'card.metadata': 'live',
  'card.progressive-disclosure': 'live',
  'card.capability-actions': 'partial',
  'organise.search': 'live',
  'organise.filters': 'live',
  'organise.sort': 'live',
  'organise.group': 'live',
  'organise.paged-loading': 'live',
  'selection.batch-actions': 'live',
  'activity.quick-create': 'live',
  'activity.calendar': 'live',
  'activity.threads': 'live',
  'activity.attachments': 'live',
  'activity.audit': 'live',
  'activity.pin-read': 'live',
  'activity.reminders': 'live',
  'activity.sla': 'live',
  'preferences.user': 'live',
  'accessibility.keyboard': 'partial',
  'state.partial-source': 'deferred',
  'source.interleave': 'deferred',
}

const marbleStates: Record<ElevateCapability, ElevateCapabilityState> = {
  'scope.record': 'live',
  'scope.mine': 'live',
  'scope.teams': 'live',
  'scope.queues': 'partial',
  'card.provenance': 'partial',
  'card.metadata': 'partial',
  'card.progressive-disclosure': 'deferred',
  'card.capability-actions': 'partial',
  'organise.search': 'deferred',
  'organise.filters': 'partial',
  'organise.sort': 'partial',
  'organise.group': 'partial',
  'organise.paged-loading': 'deferred',
  'selection.batch-actions': 'deferred',
  'activity.quick-create': 'live',
  'activity.calendar': 'deferred',
  'activity.threads': 'deferred',
  'activity.attachments': 'deferred',
  'activity.audit': 'live',
  'activity.pin-read': 'deferred',
  'activity.reminders': 'deferred',
  'activity.sla': 'partial',
  'preferences.user': 'partial',
  'accessibility.keyboard': 'partial',
  'state.partial-source': 'deferred',
  'source.interleave': 'deferred',
}

export const D365_ELEVATE_PROFILE: ElevateHostProfile = {
  host: 'd365-elevate',
  revision: 'ee4c61ee',
  measuredAt: '2026-08-23',
  sourcePaths: [
    'src/types/timeline.ts',
    'src/types/timelineSettings.ts',
    'src/components/Timeline/',
    'docs/findings/2026-08-timeline-interaction-audit.md',
  ],
  capabilities: measured(d365States, {
    'card.capability-actions': 'Card metadata exposes canEdit/canDelete/canReply, but v1 still needs action-at-source refusal semantics.',
    'organise.search': 'Live, but its nine-request-per-settled-term implementation is explicitly not part of the contract.',
    'accessibility.keyboard': 'Substantial keyboard support is live; the audit still found unnamed calendar navigation controls.',
    'state.partial-source': 'The measured host reads Dataverse only; partial multi-source survival is a v1 conformance requirement.',
    'source.interleave': 'The customer-plane Marble provider is not built yet.',
  }),
}

export const MARBLE_ELEVATE_PROFILE: ElevateHostProfile = {
  host: 'marble',
  revision: 'b7533ae9',
  measuredAt: '2026-08-24',
  sourcePaths: [
    'apps/console/src/app/ShellDrawer.tsx',
    'apps/console/src/app/WorkQueuesPanel.tsx',
    'packages/ui/src/shell/DrawerShell.tsx',
  ],
  capabilities: measured(marbleStates, {
    'scope.queues': 'Queue work exists as a separate rail lens, not yet as the same Timeline scope.',
    'card.capability-actions': 'Record quick-create is governed; existing Timeline and My Work rows are otherwise display-only.',
    'organise.filters': 'Channel chips and audit toggle are live; status, priority and date filters are absent.',
    'organise.sort': 'Fixed descending order only.',
    'organise.group': 'Record-linked person lanes only.',
    'activity.sla': 'SLA exists in the service insight lens, not on the canonical activity card.',
    'preferences.user': 'Drawer tenant and scope persist; Timeline filter preferences do not.',
  }),
}

const dataverseSource = {
  id: 'dataverse:elevatedev',
  label: 'Dynamics 365',
  kind: 'dataverse' as const,
  revision: 'W/"activity-42"',
}

const marbleSource = {
  id: 'marble:customer-prod',
  label: 'Marble',
  kind: 'marble' as const,
  revision: 'work-17',
}

/** Same Dataverse meeting as rendered by both hosts: semantics stay identical. */
export const ELEVATE_SHARED_MEETING: ElevateItem = {
  contractVersion: ELEVATE_CONTRACT_VERSION,
  id: 'appointment:42',
  source: dataverseSource,
  provenance: {
    entity: 'appointment',
    id: '42',
    label: 'Customer review',
    destination: { state: 'available', deepLink: '/appointment/42' },
  },
  kind: 'meeting',
  channel: 'teams',
  title: 'Customer review',
  summary: 'Review the proposal and agree next actions.',
  time: { effectiveAt: '2026-08-24T09:00:00Z', startsAt: '2026-08-24T09:00:00Z', endsAt: '2026-08-24T09:30:00Z' },
  actors: [{ id: 'person:hans', label: 'Hans Peeters', role: 'owner', kind: 'person' }],
  related: [{ entity: 'account', id: 'acme', label: 'Acme' }],
  status: { id: 'scheduled', label: 'Scheduled' },
  priority: { id: 'normal', label: 'Normal' },
  unread: false,
  pinned: true,
  affordances: { attachmentCount: 1, threadCount: 0, auditCount: 2 },
  actions: [
    { id: 'open', label: 'Open', sourceId: dataverseSource.id, state: 'available', batch: 'single' },
    { id: 'complete', label: 'Complete', sourceId: dataverseSource.id, state: 'available', batch: 'source-split' },
  ],
}

export const ELEVATE_MARBLE_WORK: ElevateItem = {
  contractVersion: ELEVATE_CONTRACT_VERSION,
  id: 'approval:17',
  source: marbleSource,
  provenance: {
    entity: 'approvals_request',
    id: '17',
    label: 'Quote discount',
    destination: { state: 'available', deepLink: '/approvals/17' },
  },
  kind: 'approval',
  title: 'Approve quote discount',
  summary: 'A 12% discount is outside the requestor’s authority.',
  time: { effectiveAt: '2026-08-24T09:00:00Z', dueAt: '2026-08-24T12:00:00Z' },
  actors: [{ id: 'person:christopher', label: 'Christopher', role: 'assignee', kind: 'person' }],
  related: [{ entity: 'cpq_quote', id: 'q-17', label: 'Q-17' }],
  status: { id: 'pending', label: 'Pending' },
  priority: { id: 'high', label: 'High' },
  sla: { state: 'at-risk', label: 'Due in 3 hours', dueAt: '2026-08-24T12:00:00Z' },
  actions: [
    { id: 'open', label: 'Open', sourceId: marbleSource.id, state: 'available', batch: 'single' },
    { id: 'complete', label: 'Complete', sourceId: marbleSource.id, state: 'available', batch: 'source-split' },
    { id: 'reassign', label: 'Reassign', sourceId: marbleSource.id, state: 'refused', reason: 'You cannot reassign this approval.', refresh: 'item' },
  ],
}

/* ── Documents, the third pane (#1465 comment 50168) ────────────────────────
 *
 * Two sources on purpose. The whole partial-source half of the acceptance is
 * unobservable with one: a folder served by a single mount is either there or
 * gone, and "some of your files are missing and here is which mount" cannot be
 * said at all. So the canonical folder is sovereign bytes PLUS a linked M365
 * mount, which is also the only file topology this repo actually has doors for
 * (`documents.external_link` references a file in the customer's own tenant;
 * `engagements.file` / `afd.project_file` hold sovereign bytes).
 */

const sovereignFiles = {
  id: 'marble:documents',
  label: 'Marble',
  kind: 'marble' as const,
  revision: 'documents-8814',
}

const linkedFiles = {
  id: 'sharepoint:contoso',
  label: 'SharePoint',
  kind: 'other' as const,
  revision: 'graph-etag-91',
}

export const DOCUMENTS_SOVEREIGN_SOURCE = sovereignFiles
export const DOCUMENTS_LINKED_SOURCE = linkedFiles

/** A folder: no size, no file type, and a server-declared child count. */
export const ELEVATE_DOCUMENT_FOLDER: DocumentEntry = {
  id: 'folder:contracts',
  kind: 'folder',
  name: 'Contracts',
  source: sovereignFiles,
  modifiedAt: '2026-09-01T11:00:00Z',
  childCount: 4,
  actions: [
    { id: 'open', label: 'Open', sourceId: sovereignFiles.id, state: 'available', batch: 'single' },
  ],
}

/** Sovereign bytes: every act the adapter can route, and one it cannot. */
export const ELEVATE_DOCUMENT_SEALED: DocumentEntry = {
  id: 'document:msa',
  kind: 'file',
  name: 'Master Services Agreement.pdf',
  source: sovereignFiles,
  fileType: 'pdf',
  sizeBytes: 284_318,
  modifiedAt: '2026-09-03T08:12:00Z',
  provenance: {
    entity: 'core_organization',
    id: 'acme',
    label: 'Acme',
    destination: { state: 'available', deepLink: '/core_organization/acme' },
  },
  actions: [
    { id: 'open', label: 'Open', sourceId: sovereignFiles.id, state: 'available', batch: 'single' },
    { id: 'preview', label: 'Preview', sourceId: sovereignFiles.id, state: 'available', batch: 'single' },
    { id: 'download', label: 'Download', sourceId: sovereignFiles.id, state: 'available', batch: 'source-split' },
    /* `documents.document` is append-only — `doc_immutable` raises on UPDATE and
       DELETE — so a sealed document genuinely cannot be replaced. The refusal is
       the schema's, said out loud, not a client guess. */
    { id: 'replace', label: 'Replace', sourceId: sovereignFiles.id, state: 'refused', reason: 'A sealed document is evidence and cannot be replaced.', refresh: 'item' },
  ],
}

/** A file in the customer's own M365 tenant: reference, never replicate. */
export const ELEVATE_DOCUMENT_LINKED: DocumentEntry = {
  id: 'link:proposal',
  kind: 'file',
  name: 'Renewal proposal.docx',
  source: linkedFiles,
  fileType: 'docx',
  sizeBytes: 51_204,
  modifiedAt: '2026-08-28T15:40:00Z',
  provenance: {
    entity: 'core_organization',
    id: 'acme',
    label: 'Acme',
    destination: { state: 'refused', reason: 'This file opens in SharePoint, not in Marble.' },
  },
  actions: [
    { id: 'open', label: 'Open', sourceId: linkedFiles.id, state: 'available', batch: 'single' },
    /* The bytes never traverse Marble (`documents/0009`), so there is nothing
       here to hand a browser. Absent would have been wrong: people look for
       Download, and "there is no such button" reads as a bug rather than as the
       sovereignty posture doing exactly what it was configured to do. */
    { id: 'download', label: 'Download', sourceId: linkedFiles.id, state: 'refused', reason: 'Marble only holds a link to this file; download it from SharePoint.', refresh: 'item' },
  ],
}

/**
 * The split Add, as a server would declare it for that folder.
 *
 * THREE kinds, not four. `folder` is absent because nothing in this repo can
 * create one — and absence is the correct rendering of an act that does not
 * exist, where a refusal would imply a door that is merely shut to this caller.
 */
export const ELEVATE_DOCUMENT_ADDS: DocumentAddOffer[] = [
  { kind: 'upload', label: 'Upload a file', sourceId: sovereignFiles.id, state: 'available' },
  { kind: 'link', label: 'Link an existing file', sourceId: linkedFiles.id, state: 'refused', reason: 'This deployment is sovereign-strict, so files cannot be linked to a foreign processor.' },
  { kind: 'template', label: 'Generate from a template', sourceId: sovereignFiles.id, state: 'available' },
]
